/** Overdue deadline display for task / task-template list tables */

import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import { TASK_STATUS_PENDING_COLOR, getStatusAccentSoftPillSx } from "./taskTemplateChipStyles";

/** Same green as Completed status chips in taskTemplateChipStyles */
const TASK_REMAINING_DEADLINE_CHIP_COLOR = "#0E9046";

const MS_MINUTE = 60 * 1000;
const MS_HOUR = 60 * MS_MINUTE;
const MS_DAY = 24 * MS_HOUR;

export const hasDeadlineValue = (deadlineAt, deadlineDisplay) =>
  resolveDeadlineInstant(deadlineAt, deadlineDisplay) != null;

/** Prefer ISO `deadlineAt`; fall back to parsing the list label in `deadline`. */
export const resolveDeadlineInstant = (deadlineAt, deadlineDisplay) => {
  if (deadlineDisplay === "No") return null;

  if (deadlineAt != null && deadlineAt !== "") {
    const fromAt = deadlineAt instanceof Date ? new Date(deadlineAt) : new Date(deadlineAt);
    if (!Number.isNaN(fromAt.getTime())) return fromAt;
  }

  if (deadlineDisplay && String(deadlineDisplay).trim() && deadlineDisplay !== "No") {
    const fromLabel = new Date(deadlineDisplay);
    if (!Number.isNaN(fromLabel.getTime())) return fromLabel;
  }

  return null;
};

const parseDeadlineInstant = (deadlineAt, deadlineDisplay) =>
  resolveDeadlineInstant(deadlineAt, deadlineDisplay);

const buildRelativeDeadlineAmount = (diffMs) => {
  const totalMinutes = Math.max(1, Math.floor(diffMs / MS_MINUTE));
  const totalHours = Math.floor(diffMs / MS_HOUR);
  const totalDays = Math.floor(diffMs / MS_DAY);

  if (totalHours < 1) {
    return totalMinutes === 1 ? "1 minute" : `${totalMinutes} minutes`;
  }

  if (totalHours < 24) {
    return totalHours === 1 ? "1 hour" : `${totalHours} hours`;
  }

  return totalDays === 1 ? "1 day" : `${totalDays} days`;
};

/** Format a deadline instant in the user's browser timezone (same as Created At). */
export const formatDeadlineDateTime = (value) => {
  const d =
    value instanceof Date && !Number.isNaN(value.getTime())
      ? value
      : parseDeadlineInstant(value);
  if (!d || Number.isNaN(d.getTime())) return "No";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const normalizeStatusKey = (status) =>
  String(status || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const COMPLETED_KEYS = new Set(["complete", "completed"]);
const CANCELLED_KEYS = new Set(["cancelled", "canceled"]);
const OVERDUE_STATUS_KEYS = new Set(["over due", "overdue"]);

export const isTaskStatusComplete = (status) => COMPLETED_KEYS.has(normalizeStatusKey(status));

export const isTaskStatusCancelled = (status) => CANCELLED_KEYS.has(normalizeStatusKey(status));

export const isTaskStatusOverdue = (status) => OVERDUE_STATUS_KEYS.has(normalizeStatusKey(status));

/**
 * Builds human-readable overdue amount: minutes, hours, or days (never weeks).
 * @returns {{ amountLabel: string } | null}
 */
export const getOverdueDeadlineDisplay = (deadlineAt, status, deadlineDisplay) => {
  if (isTaskStatusComplete(status) || isTaskStatusCancelled(status)) return null;

  const deadlineInstant = parseDeadlineInstant(deadlineAt, deadlineDisplay);
  if (!deadlineInstant) return null;

  const now = new Date();
  const diffMs = now.getTime() - deadlineInstant.getTime();
  if (diffMs <= 0) return null;

  return { amountLabel: buildRelativeDeadlineAmount(diffMs) };
};

/**
 * Remaining time until deadline (future, not completed).
 * Returns a +day chip only when the deadline is 24+ hours away; under 24 hours the
 * list cell uses plain date/time via {@link formatDeadlineListLabel}.
 * @returns {{ amountLabel: string } | null}
 */
export const getRemainingDeadlineDisplay = (deadlineAt, status, deadlineDisplay) => {
  if (isTaskStatusComplete(status) || isTaskStatusCancelled(status)) return null;

  const deadlineInstant = parseDeadlineInstant(deadlineAt, deadlineDisplay);
  if (!deadlineInstant) return null;

  const now = new Date();
  const diffMs = deadlineInstant.getTime() - now.getTime();
  if (diffMs <= 0) return null;

  if (diffMs < MS_DAY) return null;

  return { amountLabel: buildRelativeDeadlineAmount(diffMs) };
};

/** Same red as Pending status (legacy Over Due color). */
export const TASK_OVERDUE_CHIP_COLOR = TASK_STATUS_PENDING_COLOR;
export const TASK_COMPLETED_INTENSE_COLOR = "#22c55e";
export const TASK_OVERDUE_INTENSE_COLOR = TASK_OVERDUE_CHIP_COLOR;

/**
 * List label for deadline — always from `deadlineAt` in the browser timezone.
 * Ignores server-preformatted `deadline` strings (those use the server's TZ).
 */
export const formatDeadlineListLabel = (deadlineDisplay, deadlineAt) => {
  const instant = resolveDeadlineInstant(deadlineAt, deadlineDisplay);
  if (!instant) return "No";
  return formatDeadlineDateTime(instant);
};

/** Ensure list rows carry ISO `deadlineAt` and a browser-local `deadline` label. */
export const normalizeTaskListRow = (row) => {
  if (!row || typeof row !== "object") return row;
  const instant = resolveDeadlineInstant(row.deadlineAt, row.deadline);
  const deadlineAt = instant ? instant.toISOString() : row.deadlineAt ?? null;
  const deadline = instant ? formatDeadlineDateTime(instant) : "No";
  return {
    ...row,
    deadlineAt,
    deadline,
  };
};

export const normalizeTaskListRows = (rows) =>
  (Array.isArray(rows) ? rows : []).map((row) => normalizeTaskListRow(row));

/** Bitrix-style soft pill: light pink/red background with red text (light + dark). */
export const getOverdueDeadlineChipSx = (theme) => ({
  ...getStatusAccentSoftPillSx(theme, TASK_OVERDUE_CHIP_COLOR),
  fontWeight: 600,
  height: "auto",
  borderRadius: "999px",
  "& .MuiChip-label": {
    px: 1.1,
    py: 0.45,
    display: "block",
    color: "inherit",
  },
});

/** Green remaining-time chip (same green family as Completed status). */
export const getRemainingDeadlineChipSx = (theme) => ({
  bgcolor: TASK_REMAINING_DEADLINE_CHIP_COLOR,
  color: theme.palette.getContrastText(TASK_REMAINING_DEADLINE_CHIP_COLOR),
  fontWeight: 600,
  height: "auto",
  borderRadius: "999px",
  "& .MuiChip-label": {
    px: 1.1,
    py: 0.45,
    display: "block",
    color: "inherit",
  },
});

const RelativeDeadlineChip = ({ amountLabel, sign, chipSx }) => (
  <Chip
    size="small"
    label={
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.55,
          lineHeight: 1.2,
          color: "inherit",
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: "0.95rem",
            fontWeight: 700,
            lineHeight: 1,
            color: "inherit",
          }}
        >
          {sign}
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: "0.875rem",
            fontWeight: 600,
            letterSpacing: "0.01em",
            color: "inherit",
          }}
        >
          {amountLabel}
        </Box>
      </Box>
    }
    sx={(theme) => chipSx(theme)}
  />
);

/** Red overdue chip with larger minus sign and amount text */
export const OverdueDeadlineChip = ({ amountLabel }) => (
  <RelativeDeadlineChip amountLabel={amountLabel} sign="−" chipSx={getOverdueDeadlineChipSx} />
);

/** Green remaining-time chip with plus sign */
export const RemainingDeadlineChip = ({ amountLabel }) => (
  <RelativeDeadlineChip
    amountLabel={amountLabel}
    sign="+"
    chipSx={getRemainingDeadlineChipSx}
  />
);

/**
 * Format a relative template deadline ({ amount, unit }) as a plain label like
 * "3 days", "1 week", "30 minutes". Returns "—" when no offset is set.
 * Used by the Task Templates list (no +/- sign, no relative-to-now chip).
 */
export const formatTemplateDeadlineOffsetLabel = (offset) => {
  if (!offset || typeof offset !== "object") return "—";
  const amount = Number(offset.amount);
  if (!Number.isFinite(amount) || amount <= 0) return "—";
  const singular = String(offset.unit || "days").replace(/s$/, "");
  return `${amount} ${amount === 1 ? singular : `${singular}s`}`;
};

/** Milliseconds for a template relative deadline offset (shortest offset = smallest value). */
export const deadlineOffsetDurationMs = (offset) => {
  if (!offset || typeof offset !== "object") return Number.POSITIVE_INFINITY;
  const amount = Number(offset.amount);
  if (!Number.isFinite(amount) || amount <= 0) return Number.POSITIVE_INFINITY;
  switch (String(offset.unit || "days")) {
    case "minutes":
      return amount * MS_MINUTE;
    case "hours":
      return amount * MS_HOUR;
    case "weeks":
      return amount * 7 * MS_DAY;
    case "days":
    default:
      return amount * MS_DAY;
  }
};

/**
 * Whether the task list deadline cell shows the minus overdue badge
 * (mirrors {@link renderTaskDeadlineCell} — real time past deadline for all tasks).
 */
export const taskListShowsOverdueBadge = (row, now = new Date()) => {
  if (isTaskStatusCancelled(row?.status) || isTaskStatusComplete(row?.status)) return false;

  const deadlineInstant = parseDeadlineInstant(row?.deadlineAt, row?.deadline);
  if (!deadlineInstant) return false;

  return deadlineInstant.getTime() < now.getTime();
};

/** Overdue severity in ms for pending list sort (least minus first). */
export const getTaskListOverdueSeverityMs = (row, now = new Date()) => {
  if (isTaskStatusCancelled(row?.status) || isTaskStatusComplete(row?.status)) return 0;

  const deadlineInstant = parseDeadlineInstant(row?.deadlineAt, row?.deadline);
  if (!deadlineInstant) return 0;

  const diffMs = now.getTime() - deadlineInstant.getTime();
  return diffMs > 0 ? diffMs : 0;
};

/** Upcoming deadline sort key — soonest instant first; no deadline sorts last in tier. */
export const getTaskListUpcomingDeadlineSortKey = (row) => {
  const instant = resolveDeadlineInstant(row?.deadlineAt, row?.deadline);
  if (!instant) return Number.POSITIVE_INFINITY;
  return instant.getTime();
};

/**
 * Template list deadline sort key — shortest offset first; legacy absolute deadline fallback.
 */
export const getTemplateListDeadlineSortKey = (row) => {
  const offsetMs = deadlineOffsetDurationMs(row?.deadlineOffset);
  if (offsetMs !== Number.POSITIVE_INFINITY) return offsetMs;
  return getTaskListUpcomingDeadlineSortKey(row);
};

/** Deadline cell for task list table (tasks tab only). */
export const renderTaskDeadlineCell = (row) => {
  if (isTaskStatusCancelled(row?.status)) {
    return formatDeadlineListLabel(row.deadline, row.deadlineAt);
  }

  const overdue = getOverdueDeadlineDisplay(row.deadlineAt, row.status, row.deadline);
  if (overdue) {
    return <OverdueDeadlineChip amountLabel={overdue.amountLabel} />;
  }

  return formatDeadlineListLabel(row.deadline, row.deadlineAt);
};

/** Split timestamp into date + time lines for list pills (browser timezone). */
export const formatTaskTimestampTwoLines = (value) => {
  if (value == null || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return {
    dateLine: d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
    timeLine: d.toLocaleString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }),
  };
};

/** Neutral, readable secondary text for Modified On time line. */
export const getTaskModifiedOnTextColor = (theme) => theme.palette.text.secondary;

/** Subtle neutral pill on the date line — readable, not alarming. */
export const getTaskModifiedOnDatePillSx = (theme) => ({
  color: theme.palette.text.primary,
  bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.045)",
  border: `1px solid ${
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)"
  }`,
});

export const TaskModifiedOnPill = ({ dateLine, timeLine }) => (
  <Box
    sx={{
      display: "inline-flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 0.6,
      maxWidth: "100%",
      lineHeight: 1.25,
    }}
  >
    <Box
      component="span"
      sx={(theme) => ({
        display: "inline-block",
        px: 1.15,
        py: 0.35,
        borderRadius: "8px",
        ...getTaskModifiedOnDatePillSx(theme),
      })}
    >
      <Typography
        component="span"
        sx={{
          fontSize: "0.875rem",
          fontWeight: 600,
          lineHeight: 1.25,
          color: "inherit",
          whiteSpace: "nowrap",
        }}
      >
        {dateLine}
      </Typography>
    </Box>
    <Typography
      component="span"
      sx={(theme) => ({
        fontSize: "0.8125rem",
        fontWeight: 600,
        lineHeight: 1.25,
        color: getTaskModifiedOnTextColor(theme),
        whiteSpace: "nowrap",
        pl: 0.9,
      })}
    >
      {timeLine}
    </Typography>
  </Box>
);

/** Modified On list cell — date/time only (no modifier name). */
export const renderTaskModifiedOnCell = (row) => {
  const parts = formatTaskTimestampTwoLines(row?.updatedAt);
  if (!parts) return "—";
  return <TaskModifiedOnPill dateLine={parts.dateLine} timeLine={parts.timeLine} />;
};
